import type { EvidenceKind } from './types'

/**
 * Hierarchical TanStack Query keys for vCRO.
 *
 * The ESA pattern (lib/esa/query-keys.ts): NO `mode` and NO `workspaceId` in
 * any key. Tenant isolation is WorkspaceCacheGuard (queryClient.clear() on a
 * workspace or company switch) plus the `x-workspace-id` header fetchApi
 * attaches. Keys carry the resource and its request only.
 */
export const vcroKeys = {
  all: () => ['vcro'] as const,

  people: {
    all: () => [...vcroKeys.all(), 'people'] as const,
    /** `qs` is the whole request query string. */
    list: (qs: string) => [...vcroKeys.people.all(), 'list', qs] as const,
  },

  /** Wave 2: the organisation (Riskometer, Where the risk sits, Workspaces). */
  org: {
    all: () => [...vcroKeys.all(), 'org'] as const,
    overview: () => [...vcroKeys.org.all(), 'overview'] as const,
    trend: (from: string, to: string) => [...vcroKeys.org.all(), 'trend', from, to] as const,
    dimensions: () => [...vcroKeys.org.all(), 'dimensions'] as const,
    /** `qs` is the whole request query string. */
    breakdown: (qs: string) => [...vcroKeys.org.all(), 'breakdown', qs] as const,
    heatmap: (dimension: string) => [...vcroKeys.org.all(), 'heatmap', dimension] as const,
    concentration: () => [...vcroKeys.org.all(), 'concentration'] as const,
    movers: (dimension: string) => [...vcroKeys.org.all(), 'movers', dimension] as const,
    outcomes: (dimension: string | null, bucket: string | null) =>
      [...vcroKeys.org.all(), 'outcomes', dimension ?? 'org', bucket ?? 'org'] as const,
    workspaces: () => [...vcroKeys.org.all(), 'workspaces'] as const,
  },

  /** Wave 3: act (settings, alerts, watchlists, actions and their runs). */
  act: {
    all: () => [...vcroKeys.all(), 'act'] as const,
    settings: () => [...vcroKeys.act.all(), 'settings'] as const,
    alerts: (since: string) => [...vcroKeys.act.all(), 'alerts', since] as const,
    suggested: () => [...vcroKeys.act.all(), 'watchlists', 'suggested'] as const,
    watchlists: () => [...vcroKeys.act.all(), 'watchlists', 'list'] as const,
    actions: () => [...vcroKeys.act.all(), 'actions'] as const,
    runs: () => [...vcroKeys.act.all(), 'runs'] as const,
    courses: () => [...vcroKeys.act.all(), 'courses'] as const,
  },

  /** Wave 4 (W4-KNOW): what feeds the score, how it is built, and Getting started. */
  know: {
    all: () => [...vcroKeys.all(), 'know'] as const,
    signals: () => [...vcroKeys.know.all(), 'signals'] as const,
    model: () => [...vcroKeys.know.all(), 'model'] as const,
    gettingStarted: () => [...vcroKeys.know.all(), 'gettingStarted'] as const,
  },

  person: {
    all: (recipientId: string) => [...vcroKeys.all(), 'person', recipientId] as const,
    detail: (recipientId: string) => [...vcroKeys.person.all(recipientId), 'detail'] as const,
    trend: (recipientId: string, from: string, to: string) =>
      [...vcroKeys.person.all(recipientId), 'trend', from, to] as const,
    timeline: (recipientId: string, kind: EvidenceKind | null) =>
      [...vcroKeys.person.all(recipientId), 'timeline', kind ?? 'all'] as const,
  },
}
